import { AerialImage, ObbAnnotation } from "../api/Application";
import { RectSize, LatLng, Pixel, PixelOffset } from "./Geography";

/**
 * Oriented Bounding Box in XYWHA format
 */
export interface Obb {
    x:      number; // center   X in pixels
    y:      number; // center   Y in pixels
    width:  number; // box      width
    height: number; // box      height
    angle:  number; // rotation (radians)
}

export function createVehiclePolygon( obb: Obb ): Pixel[] {
    // extract vehicle properties (all in pixel coordinates)
    const x = obb.x;
    const y = obb.y;
    const w = obb.width;
    const h = obb.height;
    const a = obb.angle; // already in radians

    // calculate half dimensions for centering
    const wHalf = w / 2;
    const hHalf = h / 2;

    // define rectangle corners (before rotation)
    const corners = [
        [-wHalf, -hHalf], // top-left
        [ wHalf, -hHalf], // top-right
        [ wHalf,  hHalf], // bottom-right
        [-wHalf,  hHalf]  // bottom-left
    ];

    // transform each corner: rotate then translate
    const rotated = corners.map( ( [x1, y1] ) => {        
        // standard 2d rotation formula
        const rotatedX = x1 * Math.cos( a ) - y1 * Math.sin( a );
        const rotatedY = x1 * Math.sin( a ) + y1 * Math.cos( a );

        // move from origin to actual position
        const x2 = x + rotatedX;
        const y2 = y + rotatedY;
        return [ x2, y2 ] as Pixel;
    } );
    rotated.push( rotated[0] );
    return rotated;
}

export function createVehiclePolygonNormalized( image: { width: number, height: number }, obb: Obb ): Pixel[] {
    //the normalized obb is expressed as a fraction of the image's height and width
    const x = obb.x      * image.width;
    const y = obb.y      * image.height;
    const w = obb.width  * image.width;
    const h = obb.height * image.height;
    const a = ( obb.angle * Math.PI ) / 180; //angle in radians

    //calc half dims for centering
    const wHalf = w / 2;
    const hHalf = h / 2;

    //define corners (before rotating them)
    const corners = [
        [-wHalf, -hHalf], //top-left
        [ wHalf, -hHalf], //top-right
        [ wHalf,  hHalf], //bottom-right
        [-wHalf,  hHalf]  //bottom-left
    ];

    //transform each corner: rotate then translate
    const rotated = corners.map( ( [x1, y1] ) => {
        const rotatedX = x1 * Math.cos( a ) - y1 * Math.sin( a );
        const rotatedY = x1 * Math.sin( a ) + y1 * Math.cos( a );
        const x2 = x + rotatedX;
        const y2 = y + rotatedY;
        return [ x2, y2 ] as Pixel;
    } );
    rotated.push( rotated[0] );
    return rotated;
}

export function createPointedVehiclePolygonNormalized( image: { width: number, height: number }, obb: Obb ): Pixel[] {
    //the normalized obb is expressed as a fraction of the image's height and width
    const x = obb.x      * image.width;
    const y = obb.y      * image.height;
    const w = obb.width  * image.width;
    const h = obb.height * image.height;
    const a = ( obb.angle * Math.PI ) / 180; //angle in radians

    //calc half dims for centering
    const nudge      = 0.5;
    const widthBack  = w / 2.0;
    const widthFront = w / 2.0 - nudge;
    const hHalf      = h / 2.0;

    //define corners (before rotating them)
    const corners = [
        [-widthBack,          -hHalf], //top-left
        [ widthFront,         -hHalf], //top-right
        [ widthFront + nudge,    0.0], //experiment
        [ widthFront,          hHalf], //bottom-right
        [-widthBack,           hHalf]  //bottom-left
    ];

    //transform each corner: rotate then translate
    const rotated = corners.map( ( [x1, y1] ) => {
        const rx = x1 * Math.cos( a ) - y1 * Math.sin( a );
        const ry = x1 * Math.sin( a ) + y1 * Math.cos( a );
        const x2 = x + rx;
        const y2 = y + ry;
        return [ x2, y2 ] as Pixel;
    } );
    rotated.push( rotated[0] );
    return rotated;
}

export function createRoundedVehiclePolygon( obb: Obb, pad = 0.0 ): LatLng[] {
    const centerX  = obb.x;
    const centerY  = obb.y;
    const width    = obb.width  + pad;
    const height   = obb.height + pad;
    const angleRad = obb.angle; //already in radians

    const halfWidth    = width / 2;
    const halfHeight   = height / 2;
    const cornerRadius = Math.min( 6, halfWidth / 2, halfHeight / 2 ); // adjust radius as needed
    const segments     = 3; // number of segments per corner - increase for smoother curves
    const points: [number, number][] = [];

    // define the straight sections and corner centers
    const corners = [
        { center: [halfWidth - cornerRadius, -halfHeight + cornerRadius], startAngle: -Math.PI / 2, endAngle: 0 }, // Top-right
        { center: [halfWidth - cornerRadius, halfHeight - cornerRadius], startAngle: 0, endAngle: Math.PI / 2 },   // Bottom-right  
        { center: [-halfWidth + cornerRadius, halfHeight - cornerRadius], startAngle: Math.PI / 2, endAngle: Math.PI }, // Bottom-left
        { center: [-halfWidth + cornerRadius, -halfHeight + cornerRadius], startAngle: Math.PI, endAngle: 3 * Math.PI / 2 } // Top-left
    ];

    // generate points for each side with rounded corners
    for( let i = 0; i < corners.length; i++ ) {
        const corner = corners[i];
        const [cx, cy] = corner.center;

        // add corner arc points
        for( let j = 0; j <= segments; j++ ) {
            const angle = corner.startAngle + ( j / segments ) * ( corner.endAngle - corner.startAngle );
            const x = cx + cornerRadius * Math.cos( angle );
            const y = cy + cornerRadius * Math.sin( angle );
            points.push( [x, y] );
        }
    }
    // apply rotation and translation to world coordinates
    const rotated = points.map( ( [x, y] ) => {
        const rotatedX = x * Math.cos( angleRad ) - y * Math.sin( angleRad );
        const rotatedY = x * Math.sin( angleRad ) + y * Math.cos( angleRad );
        const worldX = centerX + rotatedX;
        const worldY = centerY + rotatedY;
        return [worldX, worldY] as LatLng;
    } );
    rotated.push( rotated[0] );
    return rotated;
}

export function createRoundedVehiclePolygonNormalized( image: { width: number, height: number }, obb: Obb ): LatLng[] {
    const centerX  = obb.x      * image.width;
    const centerY  = obb.y      * image.height;
    const width    = obb.width  * image.width;
    const height   = obb.height * image.height;
    const angleRad = ( obb.angle * Math.PI ) / 180;

    const halfWidth = width / 2;
    const halfHeight = height / 2;
    const cornerRadius = Math.min( 6, halfWidth / 2, halfHeight / 2 ); // adjust radius as needed
    const segments = 10; // number of segments per corner - increase for smoother curves
    const points: [number, number][] = [];

    // define the straight sections and corner centers
    const corners = [
        { center: [halfWidth - cornerRadius, -halfHeight + cornerRadius], startAngle: -Math.PI / 2, endAngle: 0 }, // Top-right
        { center: [halfWidth - cornerRadius, halfHeight - cornerRadius], startAngle: 0, endAngle: Math.PI / 2 },   // Bottom-right  
        { center: [-halfWidth + cornerRadius, halfHeight - cornerRadius], startAngle: Math.PI / 2, endAngle: Math.PI }, // Bottom-left
        { center: [-halfWidth + cornerRadius, -halfHeight + cornerRadius], startAngle: Math.PI, endAngle: 3 * Math.PI / 2 } // Top-left
    ];

    // generate points for each side with rounded corners
    for( let i = 0; i < corners.length; i++ ) {
        const corner = corners[i];
        const [cx, cy] = corner.center;

        // add corner arc points
        for( let j = 0; j <= segments; j++ ) {
            const angle = corner.startAngle + ( j / segments ) * ( corner.endAngle - corner.startAngle );
            const x = cx + cornerRadius * Math.cos( angle );
            const y = cy + cornerRadius * Math.sin( angle );
            points.push( [x, y] );
        }
    }
    // apply rotation and translation to world coordinates
    const rotated = points.map( ( [x, y] ) => {
        const rotatedX = x * Math.cos( angleRad ) - y * Math.sin( angleRad );
        const rotatedY = x * Math.sin( angleRad ) + y * Math.cos( angleRad );
        const worldX = centerX + rotatedX;
        const worldY = centerY + rotatedY;
        return [worldX, worldY] as LatLng;
    } );
    rotated.push( rotated[0] );
    return rotated;
}

export function calcRectangle( rear: [number, number], front: [number, number], width: number ): [number, number][] {
    const dx        = front[0] - rear[0];
    const dy        = front[1] - rear[1];
    const angle     = Math.atan2( dy, dx );
    const halfWidth = width / 2;
    const perp      = angle + Math.PI / 2;
    const wx        = Math.cos( perp ) * halfWidth;
    const wy        = Math.sin( perp ) * halfWidth;
    return [
        [rear[0]  - wx,  rear[1] - wy],
        [rear[0]  + wx,  rear[1] + wy],
        [front[0] + wx, front[1] + wy],
        [front[0] - wx, front[1] - wy]
    ];
}

//this is an ok place for this, not great, not terrible
export type Vec3 = [number, number, number];
export interface DialogPlacement {
    side:   "left" | "right";   // which style class
    offset: PixelOffset; // px shift from the default centered position
}

export function clampTarget( target: Vec3, zoom: number, viewPortSize: RectSize, maxBounds: RectSize ): Vec3 {
    const [vW, vH] = viewPortSize;
    const [bW, bH] = maxBounds;
    const scale = 2 ** zoom;
    const halfW = vW / scale / 2;
    const halfH = vH / scale / 2;
    const clamp = ( v: number, lo: number, hi: number ) =>
        lo > hi ? ( lo + hi ) / 2 : Math.min( hi, Math.max( lo, v ) );
    return [
        clamp( target[0], halfW, bW - halfW ),
        clamp( target[1], halfH, bH - halfH ),
        0
    ];
}

export function getDialogPlacement( desired: Vec3, clamped: Vec3, zoom: number ): DialogPlacement {
    const scale = 2 ** zoom;
    const x = ( desired[0] - clamped[0] ) * scale;
    const y = ( desired[1] - clamped[1] ) * scale;
    return { side: x < 0 ? "right" : "left", offset: [x, y] as PixelOffset };
}